import type { FC, ReactNode } from 'react'

import { useRef } from 'react'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { useEntrance } from '@/components/motion/reveal'
import { SplitChars } from '@/components/motion/split-text'
import Magnetic from '@/components/motion/magnetic'
import ContactForm from '@/components/contact/contact-form'
import StoresList from '@/components/contact/stores-list'
import Accordion from '@/components/generals/accordion'
import { faqs } from '@/constants/content'
import { siteConfig } from '@/constants/site'
import { media } from '@/constants/media'

const Contact: FC = (): ReactNode => {
    const hero = useRef<HTMLElement>(null)

    useEntrance(hero, timeline => {
        timeline
            .from('[data-ct-meta]', { autoAlpha: 0, y: 12, duration: 1 }, 0)
            .from('[data-ct-title] .mask > span', { yPercent: 140, duration: 1.4, stagger: 0.045, ease: 'expo.out' }, 0.05)
            .from('[data-ct-email]', { autoAlpha: 0, y: 40, filter: 'blur(10px)', duration: 1.3, clearProps: 'filter' }, 0.5)
    })

    return (
        <Layout>
            <Seo
                title='Contact'
                description="Write to the Elio studio about an order, a repair or a collaboration, or visit our stores in Berlin, Tokyo and New York, open daily."
                ogImage={media.concreteStepsSeated}
            />

            <section ref={hero} aria-labelledby='contact-title' className='border-b border-line pb-16 pt-[calc(var(--header-height)+48px)] gutter md:pb-24 md:pt-[calc(var(--header-height)+72px)]'>
                <p data-ct-meta className='label-mono'>
                    (Contact) — Open daily
                </p>

                <h1 id='contact-title' data-ct-title className='mt-6 font-heading text-[clamp(68px,12vw,200px)] uppercase leading-[0.82]'>
                    <SplitChars text='Say hello' />
                </h1>

                <div data-ct-email className='mt-6 md:mt-4'>
                    <Magnetic strength={0.2}>
                        <a
                            href={`mailto:${siteConfig.email}`}
                            className='inline-block break-all font-serif text-[clamp(32px,5.4vw,92px)] italic leading-[1] tracking-[-0.02em] text-bone transition-colors duration-500 hover:text-acid'
                        >
                            {siteConfig.email}
                        </a>
                    </Magnetic>
                </div>
            </section>

            <section aria-label='Write to us and visit' className='py-20 gutter md:py-28'>
                <div className='grid gap-16 lg:grid-cols-12 lg:gap-12'>
                    <div className='lg:col-span-5'>
                        <h2 data-reveal='chars' className='display-m uppercase'>
                            <SplitChars text='Write to the studio' />
                        </h2>

                        <div data-reveal='fade' className='mt-10'>
                            <ContactForm />
                        </div>
                    </div>

                    <div id='stores' className='scroll-mt-28 lg:col-span-6 lg:col-start-7'>
                        <div className='mb-8 flex items-end justify-between gap-4'>
                            <h2 data-reveal='chars' className='display-m uppercase'>
                                <SplitChars text='The stores' />
                            </h2>

                            <p data-reveal='fade' className='meta-mono text-ash'>
                                Local time
                            </p>
                        </div>

                        <StoresList />
                    </div>
                </div>
            </section>

            <section id='faq' aria-labelledby='faq-title' className='border-t border-line py-24 gutter md:py-32'>
                <div className='grid gap-12 lg:grid-cols-12 lg:gap-12'>
                    <div className='lg:col-span-5'>
                        <p data-reveal='fade' className='label-mono text-acid'>
                            (FAQ)
                        </p>

                        <h2 id='faq-title' data-reveal='chars' className='mt-6 display-xl uppercase'>
                            <span className='block'>
                                <SplitChars text='Before' />
                            </span>

                            <span className='block'>
                                <SplitChars text='you ask' />
                            </span>
                        </h2>
                    </div>

                    <div data-reveal='fade' className='lg:col-span-7'>
                        <Accordion
                            items={faqs.map(item => ({
                                title: item.question,
                                content: <p>{item.answer}</p>
                            }))}
                        />
                    </div>
                </div>
            </section>
        </Layout>
    )
}

export default Contact
